import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import { CookieService } from "src/app/services/cookie.service";
import { HttpUtilService } from "src/app/services/http-util.service";
import { PasswordMismatchValidator } from "./password-mismatch-validator.directive";
import { PasswordNoLowercaseValidator } from "./password-no-lowercase-validator.directive";
import { PasswordNoNumberValidator } from "./password-no-number-validator.directive";
import { PasswordNoSymbolValidator } from "./password-no-symbol-validator.directive";
import { PasswordNoUppercaseValidator } from "./password-no-uppercase-validator.directive";
import { PasswordTooShortValidator } from "./password-too-short-validator.directive";

@Component({
  selector: "app-change-password",
  templateUrl: "./change-password.component.html",
  styleUrls: ["./change-password.component.scss"],
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
  sending = false;
  passwordChanged = false;
  showPasswords = false;
  newPasswordVal = "";
  serviceError = false;
  serviceErrorMessage = "";
  newPasswordRepeatedVal = "";
  passwordStrengthValue = 100;
  passwordStrengthDescription = "Weak";
  showSymbolCharacters = false;
  private changePass$: Subscription;
  private userToken = "";

  changePasswordForm: FormGroup<{
    email: FormControl<string | null>;
    newPassword: FormControl<string | null>;
    newPasswordRepeated: FormControl<string | null>;
  }>;

  get email(): FormControl<string> {
    return this.changePasswordForm.controls.email;
  }
  get newPassword(): FormControl<string> {
    return this.changePasswordForm.controls.newPassword;
  }
  get newPasswordRepeated(): FormControl<string> {
    return this.changePasswordForm.controls.newPasswordRepeated;
  }

  constructor(
    private cookieService: CookieService,
    private httpUtil: HttpUtilService,
    private activatedRoute: ActivatedRoute
  ) {
    this.changePasswordForm = new FormGroup({
      email: new FormControl("", Validators.email),
      newPassword: new FormControl(this.newPasswordVal, {
        validators: [
          Validators.required,
          PasswordTooShortValidator,
          PasswordNoNumberValidator,
          PasswordNoUppercaseValidator,
          PasswordNoLowercaseValidator,
          PasswordNoSymbolValidator,
        ],
        updateOn: "blur",
      }),
      newPasswordRepeated: new FormControl(this.newPasswordRepeatedVal, {
        validators: Validators.required,
        updateOn: "blur",
      }),
    },
      {
        // Record-level validations
        validators: PasswordMismatchValidator,
        updateOn: "blur",
      }
    );
  }

  ngOnInit(): void {
    const userEmail = this.cookieService.get("user_id");
    if (userEmail) {
      this.email.setValue(userEmail);
    }
    this.email.disable();
    this.activatedRoute.queryParams.subscribe((params) => {
      this.userToken = params["ut"];
    });
    this.setPasswordStrength();
  }

  public changePassword(): void {
    this.sending = true;
    this.changePass$ = this.httpUtil
      .callService({
        serviceName: "portal.updatePassword",
        payload: {
          updatePasswordServiceRq: {
            userId: this.email.value,
            passCode: this.newPassword.value,
            userToken: this.userToken,
          },
        },
      })
      .subscribe((res) => {
        this.sending = false;
        if (
          res.updatePasswordServiceRs &&
          res.updatePasswordServiceRs.status === "error"
        ) {
          this.serviceError = true;
          this.serviceErrorMessage = res.updatePasswordServiceRs.message;
        } else {
          this.passwordChanged = true;
        }
      });
  }

  public setPasswordStrength(): void {
    this.passwordStrengthValue = 100;
    // Determine password strength
    if (this.newPassword.errors) {
      if (this.newPassword.errors.passwordTooShort) {
        this.passwordStrengthValue = this.passwordStrengthValue - 20;
      }
      if (this.newPassword.errors.passwordNoUppercase) {
        this.passwordStrengthValue = this.passwordStrengthValue - 20;
      }
      if (this.newPassword.errors.passwordNoLowercase) {
        this.passwordStrengthValue = this.passwordStrengthValue - 20;
      }
      if (this.newPassword.errors.passwordNoSymbol) {
        this.passwordStrengthValue = this.passwordStrengthValue - 20;
      }
      if (this.newPassword.errors.passwordNoNumber) {
        this.passwordStrengthValue = this.passwordStrengthValue - 20;
      }
    }

    // Update password strength description based on latest value
    if (this.passwordStrengthValue > 0 && this.passwordStrengthValue <= 33) {
      this.passwordStrengthDescription = "Weak";
    } else if (
      this.passwordStrengthValue >= 34 &&
      this.passwordStrengthValue <= 67
    ) {
      this.passwordStrengthDescription = "Medium";
    } else if (this.passwordStrengthValue >= 67) {
      this.passwordStrengthDescription = "Strong";
    }
  }
  ngOnDestroy() {
    if (this.changePass$) {
      this.changePass$.unsubscribe();
    }
  }
}
